/**
 * Route coverage matrix for MobileShell bespoke swaps.
 *
 * One page per address (roadmap M-7): every route not listed here renders the
 * main page inside mobile chrome (header + tab bar), with its route guards.
 * The item list and the 13 prioritization tools were the last phone copies and
 * were removed 2026-10-30; what is left here is the main page in the
 * wide-page frame (whose parent the header's Back opens — mobileBackTarget), or
 * full screen. Add a phone page only where the main page cannot work on a
 * phone, and say why in `note`.
 */
import { getGroupForPath } from '../pages/prioritization/settings/settingsConfig';

export const MOBILE_BESPOKE_ROUTES = [
  { pattern: '/prioritization/cycles/:id/(configure|governance|aggregation|setup)', component: 'MobileAdminLeaf', note: 'main page in the wide-page frame; Back opens the cycle' },
  { pattern: '/prioritization/strategy/*', component: 'MobileAdminLeaf', note: 'main page in the wide-page frame; Back opens the Strategy hub' },
  { pattern: '/prioritization/settings/*', component: 'MobileAdminLeaf', note: 'main page in the wide-page frame; Back opens its settings group; not the settings index or group pages' },
  { pattern: '/prioritization/sessions/:id/participate', component: 'fullscreen' },
  { pattern: '/prioritization/sessions/:id/live', component: 'fullscreen' },
  { pattern: '/prioritization/sessions/:id/present', component: 'fullscreen' },
  { pattern: '/prioritization/sessions/:id/tv', component: 'fullscreen' },
] as const;

/** Settings group index paths (exact match → the main group page, no back-link frame). */
export const SETTINGS_GROUP_INDEX_PATHS = new Set([
  '/prioritization/settings/my-account',
  '/prioritization/settings/workspace-setup',
  '/prioritization/settings/people-agents',
  '/prioritization/settings/groups-access',
  '/prioritization/settings/workflows-templates',
  '/prioritization/settings/content',
  '/prioritization/settings/planning-engine',
  '/prioritization/settings/integrations-sync',
  '/prioritization/settings/reports',
  '/prioritization/settings/developer',
]);

export function isSettingsGroupIndexPath(pathname: string): boolean {
  return SETTINGS_GROUP_INDEX_PATHS.has(pathname);
}

/** A settings page below a group: framed; Back opens its group. */
export function isSettingsLeafPath(pathname: string): boolean {
  return (
    pathname.startsWith('/prioritization/settings/') && !isSettingsGroupIndexPath(pathname)
  );
}

const CYCLE_ADMIN_LEAF_RE = /^\/prioritization\/cycles\/([^/]+)\/(configure|governance|aggregation|setup)$/;

export function parseCycleAdminLeafPath(pathname: string): { cycleId: string } | null {
  const m = CYCLE_ADMIN_LEAF_RE.exec(pathname);
  return m ? { cycleId: m[1] } : null;
}

/** A strategy page below the hub: framed; Back opens the hub. */
export function isStrategyLeafPath(pathname: string): boolean {
  return pathname.startsWith('/prioritization/strategy/');
}

/**
 * Where the phone header's Back goes on a framed page: the page's parent, not
 * the browser history. One back control per phone page (audit 2026-11-02, #38);
 * null means Back steps back through history.
 */
export function mobileBackTarget(pathname: string): { href: string; label: string } | null {
  const cycleLeaf = parseCycleAdminLeafPath(pathname);
  if (cycleLeaf) return { href: `/prioritization/cycles/${cycleLeaf.cycleId}`, label: 'Cycle' };
  if (isStrategyLeafPath(pathname)) return { href: '/prioritization/strategy', label: 'Strategy' };
  if (isSettingsLeafPath(pathname)) {
    const group = getGroupForPath(pathname);
    return { href: group?.indexHref ?? '/prioritization/settings', label: group?.title ?? 'Settings' };
  }
  return null;
}
